/**
 * Funções utilitárias para formatação de dados
 */

/**
 * Função para formatar o status da senha
 * @param {string} status - O status da senha
 * @returns {string} - O status formatado
 */
export const formatarStatus = (status) => {
  const statusMap = {
    'AGUARDANDO': 'Aguardando',
    'CHAMADA': 'Chamada',
    'EM_ATENDIMENTO': 'Em atendimento',
    'FINALIZADA': 'Finalizada',
    'CANCELADA': 'Cancelada',
    'REABERTO': 'Reaberto'
  };
  return statusMap[status] || status;
};

/**
 * Função para determinar a cor do status
 * @param {string} status - O status da senha
 * @returns {string} - A cor MUI correspondente
 */
export const getStatusColor = (status) => {
  const colorMap = {
    'AGUARDANDO': 'default',
    'CHAMADA': 'info',
    'EM_ATENDIMENTO': 'primary',
    'FINALIZADA': 'success',
    'CANCELADA': 'error',
    'REABERTO': 'warning'
  };
  return colorMap[status] || 'default';
};

/**
 * Retorna a cor correspondente ao cargo do usuário
 * @param {string} cargo - O cargo do usuário
 * @returns {string} - A cor MUI correspondente
 */
export const getCargoColor = (cargo) => {
  switch (cargo) {
    case 'ADMIN':
      return 'error';
    case 'ATENDENTE':
      return 'primary';
    case 'RECEPCAO':
      return 'success';
    default:
      return 'default';
  }
};

/**
 * Formata o cargo para exibição
 * @param {string} cargo - O cargo do usuário em formato de constante
 * @returns {string} - O cargo formatado para exibição
 */
export const formatarCargo = (cargo) => {
  const formatacao = {
    'ADMIN': 'Administrador',
    'ATENDENTE': 'Atendente',
    'RECEPCAO': 'Recepção'
  };
  return formatacao[cargo] || cargo;
};

/**
 * Retorna uma imagem padrão em formato base64 para ser usada como fallback
 * @returns {string} - Data URL de uma imagem padrão
 */
export const getDefaultImage = () => {
  // Base64 de uma imagem padrão de usuário simples (pequena)
  return 'data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHZpZXdCb3g9IjAgMCAyNCAyNCI+PHBhdGggZmlsbD0iIzYxNjE2MSIgZD0iTTEyIDJDNi40OCAyIDIgNi40OCAyIDEyczQuNDggMTAgMTAgMTAgMTAtNC40OCAxMC0xMFMxNy41MiAyIDEyIDJ6bTAgM2MxLjY2IDAgMyAxLjM0IDMgM3MtMS4zNCAzLTMgMy0zLTEuMzQtMy0zIDEuMzQtMyAzLTN6bTAgMTQuMmMtMi41IDAtNC43MS0xLjI4LTYtMy4yMi4wMy0xLjk5IDQtMy4wOCA2LTMuMDggMS45OSAwIDUuOTcgMS4wOSA2IDMuMDgtMS4yOSAxLjk0LTMuNSAzLjIyLTYgMy4yMnoiPjwvcGF0aD48L3N2Zz4=';
};

/**
 * Gera a URL completa para uma imagem
 * @param {string|null} imagePath - Caminho da imagem
 * @returns {string} URL completa da imagem
 */
export const getImageUrl = (imagePath) => {
  // Se não houver caminho, retorna imagem padrão
  if (!imagePath) {
    return getDefaultImage();
  }
  
  // Obtém o baseUrl da aplicação
  const baseUrl = process.env.REACT_APP_API_URL || 'http://localhost:3001';
  
  // Verifica se a URL já é absoluta
  if (imagePath.startsWith('http')) {
    return imagePath;
  }
  
  // Para imagens de perfil
  if (imagePath.includes('/profile-images/')) {
    // Extrair apenas o nome do arquivo
    const filename = imagePath.split('/').pop();
    
    // Construir duas URLs alternativas para tentar
    // Primeiro tenta com o endpoint específico da API
    return `${baseUrl}/usuarios/imagem/${filename}`;
  }
  
  // Para outros tipos de imagem (uploads genéricos)
  if (imagePath.startsWith('/uploads/')) {
    return `${baseUrl}${imagePath}`;
  } else if (imagePath.startsWith('/')) {
    return `${baseUrl}${imagePath}`;
  } else {
    return `${baseUrl}/${imagePath}`;
  }
};

/**
 * Compara valores para ordenação de listas
 * @param {Object} a - Primeiro objeto a comparar
 * @param {Object} b - Segundo objeto a comparar
 * @param {string} orderBy - Campo para ordenação
 * @returns {number} - Resultado da comparação
 */
export const compareValues = (a, b, orderBy) => {
  // Tratamento especial para campos específicos
  if (orderBy === 'cargo') {
    // Comparar texto formatado do cargo
    const cargoA = formatarCargo(a[orderBy]) || '';
    const cargoB = formatarCargo(b[orderBy]) || '';
    return cargoA.localeCompare(cargoB);
  }
  
  // Verificar se os valores são números
  if (!isNaN(a[orderBy]) && !isNaN(b[orderBy])) {
    return Number(a[orderBy]) - Number(b[orderBy]);
  }
  
  // Verificar se estamos lidando com null/undefined
  if (a[orderBy] === null || a[orderBy] === undefined) return -1;
  if (b[orderBy] === null || b[orderBy] === undefined) return 1;
  
  // Para strings, usar localeCompare
  return String(a[orderBy]).localeCompare(String(b[orderBy]));
}; 